'use client'

import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FieldLabel } from '@/components/ui/field'

/** `[mm:ss] Label: text` — minutes use `\d{2,}` (2-or-more digits), not a fixed `\d{2}`, since a
 * call can run up to Plivo's own 4-hour recording ceiling (240 minutes) and a fixed 2-digit
 * pattern would fail to parse any timestamp past 99 minutes. Must match the backend's
 * `formatTimestamp` in `deepgram-client.service.ts` symmetrically. */
const TURN_PATTERN = /^\[(\d{2,}):(\d{2})\]\s+(\w+):\s+(.*)$/

interface ParsedTurn {
  seconds: number
  timestampLabel: string
  speaker: string
  text: string
}

function parseLine(line: string): ParsedTurn | null {
  const match = TURN_PATTERN.exec(line)
  if (!match) return null
  const [, minutes, seconds, speaker, text] = match
  return {
    seconds: Number(minutes) * 60 + Number(seconds),
    timestampLabel: `${minutes}:${seconds}`,
    speaker: speaker!,
    text: text!,
  }
}

/**
 * YouTube-style transcript: each speaker turn is its own hoverable, clickable row. Clicking a row
 * calls `onSeek` with that turn's start time in seconds, letting a parent (`CallDetailDialog`) jump
 * `CallRecordingPlayer` to that exact moment via its `seekTo` ref method.
 *
 * A line that doesn't match the `[mm:ss] Label: text` shape — a transcript saved before this
 * format existed, or the single-channel mono fallback that has no per-speaker turns at all —
 * renders as plain, non-interactive text instead of being dropped or crashing the dialog.
 */
export function CallTranscript({
  transcript,
  onSeek,
}: {
  transcript: string
  onSeek: (seconds: number) => void
}) {
  const [copied, setCopied] = useState(false)

  const copyTranscript = async () => {
    // Copies the raw string (with its real `[mm:ss]` timestamps), not the rendered/parsed
    // version, so pasting it elsewhere keeps the same information.
    await navigator.clipboard.writeText(transcript)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const lines = transcript.split('\n')

  return (
    <div>
      <div className="flex items-center justify-between">
        <FieldLabel>Transcript</FieldLabel>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() => void copyTranscript()}
          aria-label="Copy transcript"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border p-2 text-sm">
        {lines.map((line, index) => {
          const turn = parseLine(line)
          if (!turn) {
            return (
              <p key={index} className="whitespace-pre-wrap p-1">
                {line}
              </p>
            )
          }
          return (
            <button
              key={index}
              type="button"
              onClick={() => onSeek(turn.seconds)}
              className="hover:bg-muted flex w-full flex-col gap-0.5 rounded-md p-1 text-left transition-colors"
            >
              <span className="font-semibold">
                [{turn.timestampLabel}] {turn.speaker}:
              </span>
              <span>{turn.text}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
